import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import handIcon from '../../Assets/images/hand.png';
import Items from './Components/Items';
import HorizontalList, { landscapeMenuHeight, menuHeight } from './Components/HorizontalList';
import { CompleteExpressQueryKiosk_company_categories } from '~Scenes/SelfServiceKiosks/Components/useExpressQuery/__queries__';
import { useI18n } from '~Hooks';
import SmallCart from './Components/SmallCart';
import { LocalItem } from '~Types';
import { landscapeNavbarHeight, navbarHeight } from '~Scenes/SelfServiceKiosks/Components/Navbar';

const Wrapper = styled.div`
  background: #fff;
  position: relative;
  width: 100vw;
  min-height: 100%;
`;

const Content = styled.div`
  padding: 50px 36px 48px 36px;
  height: calc(100vh - ${navbarHeight}px - ${menuHeight}px);
  overflow: auto;

  @media (orientation: landscape) {
    padding: 35px 24px;
    height: calc(100vh - ${landscapeNavbarHeight}px - ${landscapeMenuHeight}px);
  }
`;

const HandIcon = styled.img`
  width: 80px;
  height: 104px;

  @media (orientation: landscape) {
    width: 45px;
    height: 58px;
  }
`;

const NoMoreScrolling = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 120px 0 560px 0;

  @media (orientation: landscape) {
    padding: 67px 0 315px 0;
  }
`;

const NoMoreScrollingText = styled.div`
  color: rgb(189, 189, 192);
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  text-align: center;
`;

const Gradient = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  background: linear-gradient(-180deg, rgba(245, 245, 248, 0) 0%, rgb(255, 255, 255) 100%);
  height: 272px;
  width: 100%;
  pointer-events: none;

  @media (orientation: landscape) {
    height: 90px;
  }
`;

interface Props {
  orderId: string | null;
  venueId: string;
  items: LocalItem[];
  categories: CompleteExpressQueryKiosk_company_categories[];
  selectedCategoryId: string;
  onChangeCategory: React.Dispatch<React.SetStateAction<string>>;
  onClickItem: (id: string, instant?: boolean) => void;
  onSetDialogActiveScreen: React.Dispatch<React.SetStateAction<string>>;
}

const Category = ({
  orderId,
  venueId,
  categories,
  selectedCategoryId,
  onClickItem,
  onChangeCategory,
  onSetDialogActiveScreen,
  items,
}: Props) => {
  const activeCategory = useMemo(() => categories.find((cat) => cat.id === selectedCategoryId), [
    selectedCategoryId,
  ]);
  const { i18n } = useI18n();
  const cartVisible = items.length > 0;
  const filteredCategories = categories.filter((c) => !c.hideInCustomerOrderingFlows);
  return (
    <Wrapper>
      <HorizontalList
        categories={filteredCategories}
        categoryId={selectedCategoryId}
        setCategoryId={onChangeCategory}
      />
      <Content>
        <Items category={activeCategory} onClickItem={onClickItem} cartItems={items} />
        {activeCategory && activeCategory.items.length >= 6 && (
          <NoMoreScrolling>
            <HandIcon src={handIcon} />
            <NoMoreScrollingText>{i18n('SSK.Category.NoMoreSubTitle')}</NoMoreScrollingText>
          </NoMoreScrolling>
        )}
      </Content>
      <Gradient />
      {cartVisible && (
        <SmallCart
          orderId={orderId}
          items={items}
          venueId={venueId}
          onClick={() => onSetDialogActiveScreen('CART')}
          quantity={items.length}
        />
      )}
    </Wrapper>
  );
};

export default Category;
